// src/utils/saleCache.js
// All cache keys for both journeys live here.
// "pacis:" prefix namespaces away from any third-party keys.

const K = {
  pendingQuote: (email) => `pacis:quote:pending:${email}`,
  saleProgress: (email, quoteId) => `pacis:sale:progress:${email}:${quoteId}`,
  directJourney: (email) => `pacis:direct:${email}`,
  agentId: (email) => `pacis:agent:numericId:${email}`,
  paPendingQuote: (email) => `pacis:pa:quote:pending:${email}`,
  paSaleProgress: (email, quoteId) => `pacis:pa:sale:progress:${email}:${quoteId}`,
  motorPendingQuote: (email) => `pacis:motor:quote:pending:${email}`,
  motorSaleProgress: (email, quoteId) => `pacis:motor:sale:progress:${email}:${quoteId}`,
  travelPendingQuote: (email) => `pacis:travel:quote:pending:${email}`,
  travelSaleProgress: (email, quoteId) => `pacis:travel:sale:${email}:${quoteId}`,
};

// Keys written by the old code — purge on first mount
const LEGACY_KEYS = [
  'agentPendingQuote',
  'agentSaleCache',
];

export function purgeLegacyKeys(email) {
  LEGACY_KEYS.forEach((k) => sessionStorage.removeItem(k));
  // old direct cache used email in key
  if (email) sessionStorage.removeItem(`agentDirectSaleCache_${email}`);
  // old agent id key
  if (email) {
    localStorage.removeItem(`agentId_${email}`);
    localStorage.removeItem(`agentNumericId_${email}`);
  }
}

export function savePendingQuote(email, quoteData) {
  sessionStorage.setItem(K.pendingQuote(email), JSON.stringify(quoteData));
}

export function loadPendingQuote(email) {
  try {
    const raw = sessionStorage.getItem(K.pendingQuote(email));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function clearPendingQuote(email) {
  sessionStorage.removeItem(K.pendingQuote(email));
}

export function saveSaleProgress(email, quoteId, state) {
  if (!quoteId) return;
  sessionStorage.setItem(K.saleProgress(email, quoteId), JSON.stringify(state));
}

export function loadSaleProgress(email, quoteId) {
  if (!quoteId) return null;
  try {
    const raw = sessionStorage.getItem(K.saleProgress(email, quoteId));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function clearSaleProgress(email, quoteId) {
  sessionStorage.removeItem(K.saleProgress(email, quoteId));
}

export function saveDirectJourney(email, state) {
  // Only save post-verify state — pre-verify is ephemeral
  if (!state?.fetched?.vehicle) return;
  sessionStorage.setItem(K.directJourney(email), JSON.stringify(state));
}

export function loadDirectJourney(email) {
  try {
    const raw = sessionStorage.getItem(K.directJourney(email));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Only restore if vehicle was already verified
    return parsed?.fetched?.vehicle ? parsed : null;
  } catch { return null; }
}

export function clearDirectJourney(email) {
  sessionStorage.removeItem(K.directJourney(email));
}

export function saveAgentId(email, numericId) {
  localStorage.setItem(K.agentId(email), String(numericId));
}

export function loadAgentId(email) {
  const raw = localStorage.getItem(K.agentId(email));
  if (!raw || Number.isNaN(Number(raw))) return null;
  return Number(raw);
}

/**
 * Clears ALL sale-related cache for a given email.
 * Useful for the "Start New Sale" / "Clear & Restart" action.
 * Iterates sessionStorage to remove all pacis: sale keys for this user.
 */
export function saveTravelPendingQuote(email, quoteData) {
  sessionStorage.setItem(K.travelPendingQuote(email), JSON.stringify(quoteData));
}

export function loadTravelPendingQuote(email) {
  try {
    const raw = sessionStorage.getItem(K.travelPendingQuote(email));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function clearTravelPendingQuote(email) {
  sessionStorage.removeItem(K.travelPendingQuote(email));
}

export function saveTravelSaleProgress(email, quoteId, state) {
  if (!quoteId) return;
  sessionStorage.setItem(K.travelSaleProgress(email, quoteId), JSON.stringify(state));
}

export function loadTravelSaleProgress(email, quoteId) {
  if (!quoteId) return null;
  try {
    const raw = sessionStorage.getItem(K.travelSaleProgress(email, quoteId));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function clearTravelSaleProgress(email, quoteId) {
  if (!quoteId) return;
  sessionStorage.removeItem(K.travelSaleProgress(email, quoteId));
}

/** Drop in-progress travel quote + sale step cache after payment (agent). */
export function clearTravelQuoteCache(email, quoteId) {
  if (!email) return;
  clearTravelPendingQuote(email);
  clearTravelSaleProgress(email, quoteId);
}

/** Clear all in-session travel quote data (plan select "New Quote" / sale restart). */
export function clearTravelSession(email) {
  if (!email) return;
  clearTravelPendingQuote(email);
  const prefix = `pacis:travel:sale:${email}:`;
  const keysToRemove = [];
  for (let i = 0; i < sessionStorage.length; i += 1) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith(prefix)) keysToRemove.push(key);
  }
  keysToRemove.forEach((k) => sessionStorage.removeItem(k));
}

export function clearAllSaleData(email) {
  if (!email) return;

  // Remove direct journey
  sessionStorage.removeItem(K.directJourney(email));

  // Remove pending quote
  sessionStorage.removeItem(K.pendingQuote(email));
  sessionStorage.removeItem(K.paPendingQuote(email));
  sessionStorage.removeItem(K.motorPendingQuote(email));
  sessionStorage.removeItem(K.travelPendingQuote(email));

  // Remove all sale progress keys for this user (iterate sessionStorage)
  const prefixes = [
    `pacis:sale:progress:${email}:`,
    `pacis:pa:sale:progress:${email}:`,
    `pacis:motor:sale:progress:${email}:`,
    `pacis:travel:sale:${email}:`,
  ];
  const keysToRemove = [];
  for (let i = 0; i < sessionStorage.length; i += 1) {
    const key = sessionStorage.key(i);
    if (key && prefixes.some((p) => key.startsWith(p))) keysToRemove.push(key);
  }
  keysToRemove.forEach((k) => sessionStorage.removeItem(k));
}

export function savePaPendingQuote(email, quoteData) {
  sessionStorage.setItem(K.paPendingQuote(email), JSON.stringify(quoteData));
}

export function loadPaPendingQuote(email, { liveOnly = false } = {}) {
  try {
    const raw = sessionStorage.getItem(K.paPendingQuote(email));
    const data = raw ? JSON.parse(raw) : null;
    // In live mode, discard a stale mock quote (non-numeric quoteId like "MOCK-PA-...").
    if (liveOnly && data?.quoteId != null && !/^\d+$/.test(String(data.quoteId))) {
      sessionStorage.removeItem(K.paPendingQuote(email));
      return null;
    }
    return data;
  } catch { return null; }
}

export function clearPaPendingQuote(email) {
  sessionStorage.removeItem(K.paPendingQuote(email));
}

export function clearPaSaleProgress(email, quoteId) {
  if (!quoteId) return;
  sessionStorage.removeItem(K.paSaleProgress(email, quoteId));
}

/** Drop in-progress PA quote + sale step cache after payment (agent + customer). */
export function clearPaQuoteCache(email, quoteId) {
  if (!email) return;
  clearPaPendingQuote(email);
  clearPaSaleProgress(email, quoteId);
}

export function savePaSaleProgress(email, quoteId, state) {
  if (!quoteId) return;
  sessionStorage.setItem(K.paSaleProgress(email, quoteId), JSON.stringify(state));
}

export function loadPaSaleProgress(email, quoteId) {
  if (!quoteId) return null;
  try {
    const raw = sessionStorage.getItem(K.paSaleProgress(email, quoteId));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function saveMotorPendingQuote(email, quoteData) {
  sessionStorage.setItem(K.motorPendingQuote(email), JSON.stringify(quoteData));
}

export function loadMotorPendingQuote(email) {
  try {
    const raw = sessionStorage.getItem(K.motorPendingQuote(email));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function clearMotorPendingQuote(email) {
  sessionStorage.removeItem(K.motorPendingQuote(email));
}

export function saveMotorSaleProgress(email, quoteId, state) {
  if (!quoteId) return;
  sessionStorage.setItem(K.motorSaleProgress(email, quoteId), JSON.stringify(state));
}

export function loadMotorSaleProgress(email, quoteId) {
  if (!quoteId) return null;
  try {
    const raw = sessionStorage.getItem(K.motorSaleProgress(email, quoteId));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
